<template>
  <div>
    <fob-label :label="label" :asterisk="asterisk"></fob-label>

    <div
      class="projektleiter-container"
      :style="{ height: containerHeight, transition: 'all .5s' }"
    >
      <draggable
        v-model="data"
        @change="() => $emit('input', data)"
        :options="{
          handle: '.handle',
          draggable: '.projektleiter-item',
        }"
      >
        <transition-group name="projektleiter" tag="div">
          <div
            class="projektleiter-item"
            :class="{'pl-changing': changing}"
            v-for="(leiter, j) in data"
            :key="leiter._id"
            :id="`projektleiter-${leiter._id}`"
            style="padding-bottom: 1rem;"
          >
            <div class="box projektleiter-wrapper">
              <div v-if="data.length > 1" class="unselectable handle">
                &#x283F;
              </div>
              <div v-else class="unselectable mute-handle">
                &#x283F;
              </div>
              <div class="projektleiter">
                <div class="projektleiter-person">
                  <fob-entity-select
                    ref="projektleiter"
                    v-model="data[j].person"
                    @input="() => touch(leiter._id)"
                    :key="j"
                    :allowCreate="true"
                    label="projektleiter_person"
                    icon="user"
                    :method="finder"
                    :type="showErrors && (validation.$each[j] == null || validation.$each[j].person == null || validation.$each[j].person.$invalid) ? 'is-danger' : ''"
                    :route="personSchema.className"
                    :schema="personSchema"
                  ></fob-entity-select>

                  <button
                    class="button is-small projektleiter-remove-button has-text-carnelian"
                    :disabled="data.length == 1"
                    @click="removeProjektleiter(leiter._id)"
                  >
                    <b-tooltip square class="is-carnelian" position="is-top"
                      label="Entfernen"
                    >
                      <b-icon icon="trash"></b-icon>
                    </b-tooltip>
                  </button>
                </div>
                <div class="projektleiter-dates">
                  <fob-nullable-date
                    :placeholder="$gettext('Leitung Projektstart')"
                    v-model="data[j].startdatum"
                    @input="() => touch(leiter._id)"
                    :invalid="showErrors && (validation.$each[j] == null || validation.$each[j].startdatum == null || (validation.$each[j].startdatum.$invalid) ? true : false)"
                  ></fob-nullable-date>

                  <div style="display: grid; place-items: center; padding: 0 1rem;">
                    <translate>Leitung bis</translate>
                  </div>

                  <fob-nullable-date
                    :placeholder="$gettext('Leitung Projektende')"
                    v-model="data[j].enddatum"
                    @input="() => touch(leiter._id)"
                    :invalid="showErrors && (validation.$each[j] == null || validation.$each[j].enddatum == null || (validation.$each[j].enddatum.$invalid) ? true : false)"
                  ></fob-nullable-date>

				  <!-- Deactivate 'Rolle' for now -->
				  <!--
                  <div style="display: grid; place-items: center; padding: 0 1rem;">
                    <translate>Leitung als</translate>
                  </div>

                  <b-select
                    v-model="data[j].rolle"
                    @input="() => touch(leiter._id)"
                  >
                    <option v-for="rolle in roles" :value="rolle" :key="rolle">
                      <translate>
                        Projektleiterrolle {{ rolle }}
                      </translate>
                    </option>
                  </b-select>
				  -->
                </div>
                <div class="error-message has-text-carnelian">
                  <!--
                    this is sort of a hack. we need to force reevaluating validations,
                    that's why errorMsg is not removed from the DOM, only hidden.
                    there seems to be a major problem with reactivity in Vue

                    the no_person_1 and no_person_2 error messages should no longer appear
                    after having moved from a computed property 'errorMsg' to a method.
                    again, there was a problem with reactivity and a computed property.
                  -->
                  <div :class="{'hide-errors': !showErrors}" class="is-size-7">
                    <translate>
                      {{
                        errorMsg(j) ||
                        (data[j].person === null ? 'no_person_1' : '') ||
                        (data[j].person.name === '' ? 'no_person_2' : '')
                      }}
                    </translate>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </draggable>
    </div>

    <button
      class="button"
      :disabled="changing"
      :class="{'pl-changing': changing}"
      key="projektleiter-add-button"
      @click="addProjektleiter"
    >
      <translate>
        Projektleiter hinzufügen
      </translate>
    </button>
  </div>
</template>

<script>
import { Projektleiter } from 'fobclient/lib/entity'
import draggable from 'vuedraggable'
import AbstractPersonSchema from '@/schema/AbstractPerson'
import Label from '@/components/form/Label'
import NullableDate from '@/components/form/NullableDate'
import EntitySelect from '@/components/form/EntitySelect'

export default {
  name: 'fob-projektleiter-form',
  components: {
    draggable,
    'fob-label': Label,
    'fob-entity-select': EntitySelect,
    'fob-nullable-date': NullableDate,
  },
  data () {
    return {
      finder: {
        name: 'byMultiValueSearchRep',
        multiValue: true,
        minLength: 3
      },
      roles: [
        'GESAMTLEITUNG',
        'FACHLICHE_LEITUNG',
        'TECHNISCHE_LEITUNG',
      ],
      personSchema: AbstractPersonSchema,
      // This is only a shallow clone and will result in problems with reactivity.
      // To solve this, the EntityCreateForm has a `watch` method.
      data: [...this.value],
      // Alternative: Use structuredClone, more modern but not consistently
      // used in FOB, since FOB inception predates structuredClone
      // data: this.value.map(v => window.structuredClone(v)),
      changing: false,
      containerHeight: 'auto',
    }
  },
  props: {
    label: String,
    asterisk: {
      type: Boolean,
      default: false,
    },
    showErrors: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: Object,
      default: {},
    },
    value: {
      type: Array,
      default () {
        return []
      },
    },
  },
  methods: {
    errorMsg (j) {
      if (this.data[j]) {
        if (this.validation.$each[j] == null) {
          return ''
        } else if (this.validation.$each[j].person == null || this.validation.$each[j].person.$invalid) {
          return 'projektleiter_no_person'
        } else if (!this.validation.$each[j].startdatum.withinProjectBounds) {
          return 'startdate_out_of_scope'
        } else if (!this.validation.$each[j].enddatum.withinProjectBounds) {
          return 'enddate_out_of_scope'
        } else if (!this.validation.$each[j].startdatum.beforeEnddatum) {
          return 'enddate_before_startdate'
        }
        return ''
      } else {
        // should not happen
        return 'state out of sync'
      }
    },
    async addProjektleiter () {
      this.changing = true
      await this.$nextTick()
      this.data.push(new Projektleiter({}))

      // adjust height
      if (this.containerHeight === 'auto') {
        this.containerHeight = document
          .getElementsByClassName('projektleiter-container')[0]
          .offsetHeight
      }
      await this.$nextTick()
      let pls = [...document.getElementsByClassName('projektleiter-item')]
      let total = pls.reduce((acc, el) => acc + el.offsetHeight, 0)
      this.containerHeight = `${total}px`

      // focus input field of new projektleiter
      // (somewhat hacky because frameworks kinda suck)
      let neuerLeiter = document
        .getElementsByClassName('projektleiter-container')[0]
        .firstElementChild
        .firstElementChild
        .lastChild
      let input = neuerLeiter.getElementsByClassName('autocomplete')[0]
        .firstElementChild
        .firstElementChild
      input.focus()

      this.$emit('input', this.data)

      setTimeout(() => {
        this.changing = false
      }, 500)
    },
    async removeProjektleiter (id) {
      this.changing = true
      await this.$nextTick()
      this.data = this.data.filter(leiter => leiter._id !== id)

      // adjust height
      let pls = [...document.getElementsByClassName('projektleiter-item')]
      let total = pls.reduce((acc, el) => {
        return acc + (el.id === `projektleiter-${id}` ? 0 : el.offsetHeight)
      }, 0)
      this.containerHeight = `${total}px`

      this.$emit('input', this.data)

      setTimeout(() => {
        this.changing = false
      }, 500)
    },
    touch (id) {
      this.data.forEach(pl => {
        if (pl._id === id) {
          pl._dirty = true
          // TODO: Is `this.$emit(input, this.data)` needed here?
          // Probably only if structuredClone is used in `data` above.
        }
      })
    },
  },
}
</script>

<style scoped>
  .unselectable {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .box {
    padding-left: 0.5rem;
  }

  .pl-changing {
    transition: transform 0.5s;
  }

  .projektleiter-wrapper {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin: 0;
    padding-top: 1.75rem;
    border: 1px solid transparent;
  }

  .projektleiter-wrapper .mute-handle {
    opacity: 0.25;
    transition: opacity 500ms;
    padding: 2rem 0.25rem;
    cursor: not-allowed;
  }

  .projektleiter-wrapper .handle {
    transition: opacity 500ms;
    padding: 2rem 0.25rem;
    cursor: move;
  }

  .projektleiter-wrapper .handle:hover {
    background-color: var(--gray-1);
  }

  .projektleiter {
    flex-grow: 1;
  }

  .projektleiter-dates {
    display: flex;
    margin-top: 1rem;
  }

  .projektleiter-person {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
  }

  .projektleiter-remove-button {
    border: none;
    background-color: transparent;
  }

  .projektleiter-enter, .projektleiter-leave-to {
    transition: all 0.5s;
    opacity: 0;
    transform: translateY(100px);
    width: 800px;
  }
  /* this messes with drag-and-drop */
  /* .projektleiter-move {
    transition: transform 0.5s;
  } */
  .projektleiter-leave-active {
    position: absolute;
  }
  .projektleiter-enter-active, .projektleiter-leave-active {
    transition: all 0.5s;
  }

  .sortable-chosen {
    transform: translateX(-10px);
  }

  .sortable-chosen .projektleiter-wrapper {
    background-color: #DAEBF2;
    border: 1px solid #c2dbe6;
  }

  .sortable-chosen .handle {
    background-color: #DAEBF2 !important;
  }

  .error-message {
    display: flex;
    justify-content: left;
    align-items: center;
    margin-top: 0rem;
    margin-bottom: -1rem;
    height: 2rem;
  }

  .hide-errors {
    display: none;
  }
</style>



// WEBPACK FOOTER //
// src/components/form/Projektleiter.vue